// Persists a referral code (?ref=CODE) across SPA navigation so a user who
// lands via a referrer's link, browses the site, and later registers from
// /register (without ?ref in the URL) is still linked to the referrer.
//
// Storage layout (localStorage):
//   novelday:referral-code  -> JSON { code, storedAt }
//
// TTL: 30 days. A code older than that is treated as stale and ignored,
// which prevents a user who clicked a referral link a month ago, forgot,
// and then registered organically from being attributed to that referrer.

const STORAGE_KEY = 'novelday:referral-code';
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

/**
 * Reads and validates the ?ref= query param from a URL string (or the
 * current window location when called with no argument). Returns the
 * uppercased, trimmed code, or null if absent/blank.
 *
 * Exported for testing and for callers that already have a URL string.
 */
export const extractRefFromUrl = (urlOrSearch) => {
  let search;
  if (!urlOrSearch) {
    if (typeof window === 'undefined') return null;
    search = window.location.search;
  } else if (urlOrSearch.startsWith('?')) {
    search = urlOrSearch;
  } else {
    // Full URL — may contain a query string somewhere.
    try {
      search = new URL(urlOrSearch, window.location.origin).search;
    } catch {
      const qIdx = urlOrSearch.indexOf('?');
      search = qIdx >= 0 ? urlOrSearch.slice(qIdx) : '';
    }
  }
  const params = new URLSearchParams(search);
  const raw = params.get('ref');
  if (!raw) return null;
  const code = raw.trim().toUpperCase();
  return code || null;
};

/**
 * Stores a referral code to localStorage with a timestamp. Called from
 * App.js whenever the URL contains ?ref=CODE on any route.
 *
 * No-op in non-browser environments (SSR, tests without jsdom).
 */
export const storeReferralCode = (code) => {
  if (typeof window === 'undefined') return;
  if (!code) return;
  const normalized = String(code).trim().toUpperCase();
  if (!normalized) return;
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code: normalized, storedAt: Date.now() })
    );
  } catch {
    // localStorage may be unavailable (private browsing, quota exceeded).
    // Silently ignore — the referral code is also captured from the URL
    // on the register page directly, so this is a best-effort optimization.
  }
};

/**
 * Returns the stored referral code if present and not expired, else null.
 * Also returns null in non-browser environments.
 */
export const getStoredReferralCode = () => {
  if (typeof window === 'undefined') return null;
  let raw;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.code !== 'string' || !parsed.code) return null;
    if (typeof parsed.storedAt !== 'number') return null;
    if (Date.now() - parsed.storedAt > TTL_MS) {
      // Expired — clean up so we don't keep re-reading stale data.
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed.code;
  } catch {
    // Corrupt JSON — remove it so future reads are clean.
    window.localStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

/**
 * Removes any stored referral code. Called after a successful registration
 * so the code is not reused if the same browser registers another account
 * (e.g. a shared device).
 */
export const clearStoredReferralCode = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(STORAGE_KEY);
};

export { STORAGE_KEY, TTL_MS };
